// Dependencies
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { Column } from '../objects/column';
import { RoadbookCategory } from '../objects/roadbookCategory';
import { Preferences } from '../../profile/objects/preferences';
import { RoadbookCategoryState } from '../objects/roadbookCategoryState';
import { RoadbookCategoryLocalized } from '../objects/roadbookCategoryLocalized';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const initialState: RoadbookCategoryState = {
    columns: [
        {
            label: 'name',
            field_name: 'NAME',
            checked: true,
            order_by: 'localization__name',
            fixed_width: false,
            last_updated: false,
            width: null
        },
        {
            label: 'created-date',
            field_name: 'CREATED_DATE',
            checked: false,
            order_by: 'created_date',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'created-by',
            field_name: 'CREATED_BY',
            checked: false,
            order_by: 'creator__username',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'modified-date',
            field_name: 'MODIFIED_DATE',
            checked: true,
            order_by: 'modified_date',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'modified-by',
            field_name: 'MODIFIED_BY',
            checked: true,
            order_by: 'last_author__username',
            fixed_width: true,
            last_updated: false,
            width: null
        }
    ],
    count: 0,
    create: false,
    delete_category: null,
    edit: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    limit: 50,
    list: null,
    name_locales: {},
    order: 'asc',
    order_by: 'name',
    page: 0,
    preferences: [],
    refresh: false,
    roadbook_category: null,
    running: false,
    search: '',
};

export const roadbookCategorySlice = createSlice({
    name: 'roadbook_category',
    initialState,
    reducers: {
        addPreferences: (state, action) => {
            state.preferences = [...state.preferences, action.payload];
        },
        deleteCategory: (state, action: PayloadAction<RoadbookCategory | null | false>) => {
            if (action.payload === false) {
                state.delete_category = null;
            } else if (action.payload === null) {
                if (state.list !== null && state.delete_category !== null) {
                    const list = [...state.list];
                    for (let i = 0; i < list.length; i++) {
                        if (list[i].id === state.delete_category.id) {
                            list.splice(i, 1);
                            break;
                        }
                    }
                    state.list = list;
                    state.delete_category = null;
                }
            } else {
                state.delete_category = action.payload;
            }
        },
        moveDownColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
        },
        resetRoadbookCategory: (state) => {
            state.create = false;
            state.edit = null;
            state.name_locales = {};
            state.roadbook_category = null;
            state.refresh = !state.refresh;
        },
        setEdit: (state, action) => {
            const name_locales: Record<string, string> = {};
            state.roadbook_category?.localization.map((locale: RoadbookCategoryLocalized) => {
                name_locales[locale.locale] = locale.name;
            });
            state.edit = action.payload;
            state.name_locales = name_locales;
        },
        setExportType: (state, action) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc';
        },
        setPage: (state, action) => {
            state.list = null;
            state.page = action.payload;
        },
        setRoadbookCategory: (state, action: PayloadAction<RoadbookCategory>) => {
            const name_locales: Record<string, string> = {};
            action.payload.localization.map((locale: RoadbookCategoryLocalized) => {
                name_locales[locale.locale] = locale.name;
            });
            state.edit = null;
            state.name_locales = name_locales;
            state.roadbook_category = action.payload;
        },
        setRoadbookCategoryPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        setSearch: (state, action) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleColumn: (state, action) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleExport: (state, action) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        updateFilterCreatedDateGte: (state, action) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreator: (state, action) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterPeriod: (state, action) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action) => {
            state.filter_period_type = action.payload;
        },
        updateNameLocale: (state, action: PayloadAction<{ id: number, value: string }>) => {
            const { id, value } = action.payload;
            const name_locales = { ...state.name_locales };
            name_locales[id] = value;
            state.name_locales = name_locales;
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'ROADBOOK_CATEGORY') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'NAME':
                                    columns.push({
                                        label: 'name',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'localization__name',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'localization__name' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({
                                        label: 'created-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'created_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({
                                        label: 'created-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'creator__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({
                                        label: 'modified-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'modified_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({
                                        label: 'modified-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'last_author__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map((column) => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addDefaultCase(() => {});
    }
});

export const {
    addPreferences,
    deleteCategory,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetRoadbookCategory,
    setEdit,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setRoadbookCategory,
    setRoadbookCategoryPreferences,
    setRunning,
    setSearch,
    toggleColumn,
    toggleCreate,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    updateFilterCreatedDateGte,
    updateFilterCreator,
    updateFilterCreatedDateLte,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateNameLocale,
    setColumns,
    resizeColumn
} = roadbookCategorySlice.actions;

export default roadbookCategorySlice.reducer;